/**
 * SOLID Review
 * S — Strong. One use case: after a purchase, the account's balance — read once and kept.
 * O — Strong. Where a balance is kept is the Domain's decision.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and payment-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { IEntityBalance, ICommandBalanceRead } from '../../domain/ports/schemas/PaymentMoves';
import type { TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IConfigDepPayment } from './IConfigDepPayment';

/** One read of the account's balance (FE4c), kept; a failed read changes nothing — the screen retries. Not a round trip: no plan. */
export class ReadBalanceUseCase implements IUseCase<ICommandNothing, TPortResult<IEntityBalance>> {
  constructor(private readonly deps: IConfigDepPayment) {}

  async execute(ctx: IContextRequest, _command: ICommandNothing): Promise<TPortResult<IEntityBalance>> {
    const { decisions, step, payments } = this.deps;
    const read = await payments.balance(ctx);
    if (!read.success) return read;
    const command: ICommandBalanceRead = { credits: read.value.matchCredits };
    const kept = await step.run(ctx, (input) => decisions.balanceRead(ctx, input, command));
    if (!kept.success) return kept;
    return read;
  }
}
